import { useState, useCallback } from 'react';
import api from '../utils/api';
import { useSurvey } from '../context/SurveyContext';
import { debugLog } from '../utils/logger';

/**
 * useSurveyApi
 * Encapsulates all three patient-pref-service API calls.
 * Pages never call axios directly — all API interaction goes through this hook.
 */
export function useSurveyApi() {
    const { setSessionToken, setPatientData, setPracticeName } = useSurvey();
    const [loading, setLoading] = useState(false);
    const [error,   setError  ] = useState(null);

    const clearError = useCallback(() => setError(null), []);

    // ── POST /survey/verify-token (under /api/v1 via VITE_API_BASE_URL) ─────
    // Called on ValidatePage load with the pid token from the URL
    // Returns { firstName, practiceName, sessionToken }
    const verifyToken = useCallback(async (pid) => {
        setLoading(true);
        setError(null);
        debugLog('ui_verify_token_started', { hasPid: Boolean(pid) });
        try {
            const { data } = await api.post('/survey/verify-token', { pid });
            setSessionToken(data.sessionToken);
            setPracticeName(data.practiceName);
            debugLog('ui_verify_token_succeeded', { hasPracticeName: Boolean(data.practiceName) });
            return { firstName: data.firstName, practiceName: data.practiceName };
        } catch (err) {
            debugLog('ui_verify_token_failed', { code: err.code, status: err.status });
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [setSessionToken, setPracticeName]);

    // ── POST /survey/verify-dob (under /api/v1 via VITE_API_BASE_URL) ──────
    // Called when patient submits DOB on ValidatePage
    // Returns { firstName, lastName, phone, email, practiceName, sessionToken }
    const verifyDob = useCallback(async (dob) => {
        setLoading(true);
        setError(null);
        debugLog('ui_verify_dob_started', { hasDob: Boolean(dob) });
        try {
            const { data } = await api.post('/survey/verify-dob', { dob });
            setSessionToken(data.sessionToken);
            setPatientData({
                firstName : data.firstName,
                lastName  : data.lastName,
                phone     : data.phone,
                email     : data.email
            });
            setPracticeName(data.practiceName);
            debugLog('ui_verify_dob_succeeded', { hasPracticeName: Boolean(data.practiceName) });
            return data;
        } catch (err) {
            debugLog('ui_verify_dob_failed', { code: err.code, status: err.status });
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [setSessionToken, setPatientData, setPracticeName]);

    // ── POST /survey/submit (under /api/v1 via VITE_API_BASE_URL) ──────────
    // Called when patient submits the preferences form
    // Payload: { phone?, email?, commChannel?, callPrefDayOfWeek?, callPrefTimeOfDay?, apptPrefTimeOfDay?, techComfortLevel? }
    const submitPreferences = useCallback(async (payload) => {
        setLoading(true);
        setError(null);
        debugLog('ui_submit_preferences_started', { payloadKeys: Object.keys(payload || {}) });
        try {
            const { data } = await api.post('/survey/submit', payload);
            debugLog('ui_submit_preferences_succeeded');
            return data;
        } catch (err) {
            debugLog('ui_submit_preferences_failed', { code: err.code, status: err.status });
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { verifyToken, verifyDob, submitPreferences, loading, error, clearError };
}
